import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Star, DotsSixVertical, MapPin, CalendarBlank, ArrowClockwise, UploadSimple } from "@phosphor-icons/react";

export default function SitePhotosManager({ jobId }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/jobs/${jobId}/site-photos`);
      setPhotos(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (jobId) load(); /* eslint-disable-next-line */ }, [jobId]);

  const base = api.defaults.baseURL;
  const setCaption = (key, v) => setPhotos((ps) => ps.map((p) => (p.key === key ? { ...p, caption: v } : p)));
  const setCover = (key) => setPhotos((ps) => ps.map((p) => ({ ...p, cover: p.key === key })));
  const onDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) return;
    setPhotos((ps) => { const a = [...ps]; const [m] = a.splice(dragIdx, 1); a.splice(idx, 0, m); return a; });
    setDragIdx(null);
  };
  const save = async () => {
    setSaving(true);
    const meta = {};
    photos.forEach((p, i) => { meta[p.key] = { caption: p.caption || "", order: i, cover: !!p.cover }; });
    try { await api.put(`/jobs/${jobId}/site-photos`, { meta }); toast.success("Photo arrangement saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadPhotos = async (files) => {
    const imgs = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!imgs.length) { toast.error("Please choose image files"); return; }
    setUploading(true);
    try {
      for (const f of imgs) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/jobs/${jobId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`Uploaded ${imgs.length} photo(s)`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="space-y-3" data-testid="site-photos-manager">
      <div className="flex items-center justify-between gap-2">
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="site-photos-file-input"
          onChange={(e) => uploadPhotos(e.target.files)} />
        <Button type="button" size="sm" variant="outline" className="rounded-full h-8 text-xs" disabled={uploading}
          onClick={() => fileRef.current?.click()} data-testid="site-photos-upload-btn">
          <UploadSimple size={15} className="mr-1.5" /> {uploading ? "Uploading…" : "Upload Photos"}
        </Button>
        <Button type="button" size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={load} title="Refresh" data-testid="site-photos-refresh"><ArrowClockwise size={15} /></Button>
      </div>
      {!photos.length ? (
        <div className="text-xs text-muted-foreground border border-dashed border-border rounded-lg p-4 text-center" data-testid="site-photos-empty">
          {loading ? "Loading photos…" : "No site photos yet. Click \u201cUpload Photos\u201d to add site images, or attach photos to job notes."}
        </div>
      ) : (<>
      <p className="text-xs text-muted-foreground">Drag to reorder · tap the star to pick the cover photo · type a caption. Compass direction, distance and capture date are added automatically.</p>
      {!photos.some((p) => p.cover) && (
        <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-300 rounded-lg p-2.5" data-testid="site-photos-cover-reminder">
          <Star size={15} weight="fill" className="text-amber-500 mt-0.5 shrink-0" />
          <span>No cover photo selected. The first photo will be used on the report cover by default — tap the ★ on your preferred photo to choose the front-page image.</span>
        </div>
      )}
      <div className="space-y-2">
        {photos.map((p, idx) => (
          <div key={p.key} draggable onDragStart={() => setDragIdx(idx)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(idx)}
            className="flex gap-3 items-start border border-border rounded-lg p-2 bg-card" data-testid={`site-photo-${idx}`}>
            <DotsSixVertical size={18} className="text-muted-foreground mt-6 cursor-grab shrink-0" weight="bold" />
            <img src={`${base}${p.url}`} alt={p.filename} className="w-20 h-16 object-cover rounded border border-border shrink-0" />
            <div className="flex-1 min-w-0 space-y-1.5">
              <Input value={p.caption} onChange={(e) => setCaption(p.key, e.target.value)} placeholder={p.filename} className="h-8 text-xs" data-testid={`site-photo-caption-${idx}`} />
              <div className="flex flex-wrap gap-2 text-[10px] text-muted-foreground">
                {p.geo_label && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {p.geo_label}</span>}
                {p.captured_on && <span className="inline-flex items-center gap-1"><CalendarBlank size={11} /> {p.captured_on}</span>}
              </div>
            </div>
            <button type="button" onClick={() => setCover(p.key)} title="Set as cover photo" data-testid={`site-photo-cover-${idx}`}
              className={`shrink-0 mt-5 ${p.cover ? "text-amber-500" : "text-muted-foreground/40 hover:text-muted-foreground"}`}>
              <Star size={20} weight={p.cover ? "fill" : "regular"} />
            </button>
          </div>
        ))}
      </div>
      <Button type="button" onClick={save} disabled={saving} variant="outline" className="rounded-full w-full" data-testid="site-photos-save-btn">{saving ? "Saving…" : "Save Photo Arrangement"}</Button>
      </>)}
    </div>
  );
}
